/**
 * The browser's own "install this app" offer, surfaced as a menu item.
 *
 * ONLY WHEN THE BROWSER ACTUALLY OFFERS IT. Chromium fires
 * `beforeinstallprompt` when it judges a page installable and lets the
 * page defer that prompt to a moment of its choosing. Nothing else
 * fires it: on iOS Safari the only route is Share -> Add to Home
 * Screen, which a page cannot trigger. An "Install" button that did
 * nothing on an iPad would be worse than no button, so this returns
 * null until the event has actually arrived and the menu renders
 * nothing in the meantime. The iOS route belongs in the device runbook,
 * not in a control that cannot work.
 *
 * The event must be captured at module scope, not in an effect: it
 * fires early, often before React has mounted, and it is not re-fired.
 * A listener registered in a component would simply miss it.
 */
import { useEffect, useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();

function announce(): void {
  listeners.forEach((l) => l());
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    // Chromium shows its own mini-infobar unless the event is
    // cancelled; cancelling is what moves the offer into our menu.
    e.preventDefault();
    deferred = e as BeforeInstallPromptEvent;
    announce();
  });
  // Once installed the offer is spent, whether it came from our menu
  // or from the browser's own address-bar affordance.
  window.addEventListener('appinstalled', () => {
    deferred = null;
    announce();
  });
}

export function useInstallPrompt(): { install: () => Promise<void> } | null {
  const [available, setAvailable] = useState(deferred !== null);

  useEffect(() => {
    const onChange = () => setAvailable(deferred !== null);
    listeners.add(onChange);
    return () => {
      listeners.delete(onChange);
    };
  }, []);

  if (!available) return null;

  return {
    install: async () => {
      const event = deferred;
      if (!event) return;
      // Spent either way: Chromium refuses a second `prompt()` on the
      // same event, so keeping it would leave a menu item that works
      // once and then silently does nothing.
      deferred = null;
      announce();
      await event.prompt();
      await event.userChoice;
    },
  };
}
